import React, { useState } from 'react'
import LoginHelpModal from '@/components/auth/LoginHelpModal'
import type { Identifier } from '@/hooks/useLoginFlow'

/**
 * Small "Having trouble signing in?" link + the LoginHelpModal it
 * opens, with the modal pre-filled from whatever the customer typed
 * on Step 1. Kept as its own component so each Step 2 component
 * (LoginPasswordStep, LoginClaimStep, LoginNotFoundStep) can drop it
 * in without owning the modal-open state itself.
 *
 * Step 1 deliberately does NOT use this — at the identifier stage
 * the customer hasn't given us enough context for a useful support
 * submission yet, and the help text would compete with the "Next"
 * affordance.
 */

interface Props {
  identifier: Identifier
  /**
   * The link's wording. Defaults to the standing-alone "Help?" the other
   * steps use; the not-found step reads it as a sentence ("Raise a query and
   * we'll get back to you"), where a bare "Help?" does not fit.
   */
  label?: string
}

function LoginHelpTrigger({ identifier, label = 'Help?' }: Props): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false)

  // Translate the flow's identifier shape into the modal's prefill
  // shape. We pass whichever identifier the customer typed on Step 1.
  const prefill = {
    email: identifier.mode === 'email' ? identifier.email : '',
    customer_number:
      identifier.mode === 'customer_number' ? identifier.customerNumber : '',
  }

  return (
    <>
      <button
        type="button"
        className="login-help-trigger"
        onClick={() => setIsOpen(true)}
        aria-label="Help with signing in"
      >
        {label}
      </button>
      <LoginHelpModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        prefill={prefill}
      />
    </>
  )
}

export default LoginHelpTrigger
